import { readQueryParam } from "./readQueryParam";
import { resolveBackendBaseUrlOverride } from "./resolveBackendBaseUrlOverride";
import type { Log } from "@slot/presentation";

/**
 * The 3 backend modes a build picks between (`BootConfig.backendMode`). The values are the
 * strings a title's build environment names (slot-cannon: `VITE_BACKEND_MODE`).
 */
export enum BackendMode {
  /** Use `deployedUrl` — the deployed-target URL (staging, prod, feature branch). */
  Deployed = "deployed",
  /** Use `localhostUrl` — typically `http://localhost:3000`. */
  Localhost = "localhost",
  /** Force offline ScriptedRoundProvider — empty effective baseUrl + fixture launch-context + fixture initial-settings + fixture currency-catalog. */
  Offline = "offline",
}

/**
 * Composes `baseUrl + path`, normalizing trailing slash on the host and
 * leading slash on the path. Single source of truth for host-vs-path joining;
 * mirrors the same logic in BackendRoundProvider's private buildUrl.
 */
export function composeBackendUrl(baseUrl: string, path: string): string {
  const host = baseUrl.replace(/\/$/, "");
  const trimmedPath = path.trim();
  const normalizedPath = trimmedPath.startsWith("/") ? trimmedPath : `/${trimmedPath}`;
  return `${host}${normalizedPath}`;
}

/**
 * Resolves the effective backend base URL from (in priority order):
 *   1. ?backend=<value> URL override — only when devModeEnabled is true.
 *      `local` → localhostUrl, `deployed` (alias `staging`) → deployedUrl,
 *      an http(s):// value → that URL verbatim, `scripted` → offline. A URL
 *      is refused when the link carries a `launch` (decision 81).
 *      `scripted` also forces launchContextSourceMode = Fixture so the
 *      launch-context loader does not attempt a fetch against an empty URL.
 *      Caller honors this via `forceFixtureLaunchMode: true` in the return.
 *   2. `BootConfig.backendMode`:
 *      Offline   → empty + forceFixtureLaunchMode = true
 *      Localhost → localhostUrl
 *      Deployed  → deployedUrl
 * Empty string → `createRuntime` runs the game's fixture provider.
 *
 * PURE function — does not mutate anything. The caller is responsible for
 * applying `forceFixtureLaunchMode` to whatever stores the launch mode.
 */
export function resolveEffectiveBackendBaseUrl(input: {
  devModeEnabled: boolean;
  backendMode: BackendMode;
  localhostUrl: string;
  deployedUrl: string;
  queryString: string | null;
  log: Log;
}): { baseUrl: string; forceFixtureLaunchMode: boolean } {
  const { devModeEnabled, backendMode, localhostUrl, deployedUrl, queryString, log } = input;

  if (devModeEnabled) {
    const override = resolveBackendBaseUrlOverride(queryString, log);
    // A launch is minted by the build's own backends for themselves (decision 81): with one in the link, `?backend=`
    // names a known target or nothing, so a crafted link cannot hand the player's launch to another host.
    if (override?.kind === "url" && readQueryParam(queryString, "launch") !== null) {
      log(`[Backend] ignored ?backend=<URL>: the link carries a launch, which goes only to a named backend ("local", "staging")`);
    } else if (override !== null) {
      if (override.kind === "scripted") {
        log(`[Backend] override applied: offline mode (empty baseUrl + fixture launch-context)`);
        return { baseUrl: "", forceFixtureLaunchMode: true };
      }
      const value =
        override.kind === "local"
          ? localhostUrl.trim()
          : override.kind === "deployed"
            ? deployedUrl.trim()
            : override.value;
      log(`[Backend] override applied: effective URL="${value}" (from ?backend=)`);
      return { baseUrl: value, forceFixtureLaunchMode: false };
    }
  }

  if (backendMode === BackendMode.Offline) {
    log(`[Backend] backendMode=Offline (empty baseUrl + fixture launch-context)`);
    return { baseUrl: "", forceFixtureLaunchMode: true };
  }

  if (backendMode === BackendMode.Localhost) {
    return { baseUrl: localhostUrl.trim(), forceFixtureLaunchMode: false };
  }

  return { baseUrl: deployedUrl.trim(), forceFixtureLaunchMode: false };
}
